import React from "react";
import {
  Card,
  CardContent,
  Avatar,
  Typography,
  Box,
  LinearProgress,
  IconButton,
  Divider,
} from "@mui/material";
import { ReactComponent as VisibilityIcon } from "../assets/icons/visibility.svg";
import { colors } from "../values/colors";

const TeamCard = ({
  teamName,
  logo,
  remainingPoints,
  totalPoints,
  playersPurchased,
  totalSlots,
  onView,
}) => {
  const pointsSpent = totalPoints - remainingPoints;
  const progress = ((remainingPoints || 0) / (totalPoints || 1)) * 100;
  const slotsRemaining = (totalSlots || 0) - (playersPurchased || 0);

  const fullWidthDivider = {
    borderColor: "#CACACA",
    borderBottomWidth: "1px",
    my: 1.5,
    mx: -1.5,
  };

  return (
    <Card
      sx={{
        borderRadius: 3,
        boxShadow: 3,
        p: 1.5,
        width: 314,
        backgroundColor: "#fff",
        overflow: "hidden",
        position: "relative",
      }}
      onClick={onView}
    >
      {/* Visibility Icon at Top Right */}
      <IconButton
        size="small"
        onClick={onView}
        sx={{
          position: "absolute",
          top: 10,
          right: 10,
        }}
      >
        <VisibilityIcon fontSize="small" />
      </IconButton>

      {/* Header */}
      <Box display="flex" alignItems="center">
        <Avatar
          src={logo}
          alt={teamName}
          sx={{ width: 83, height: 83, borderRadius: 2, mr: 1 }}
          variant="rounded"
        />
        <Typography
          variant="h6"
          sx={{ fontSize: "24px", fontWeight: 500, marginTop: "1.2rem" }}
        >
          {teamName}
        </Typography>
      </Box>

      {/* Divider under header */}
      <Divider sx={fullWidthDivider} />

      <Box sx={{ padding: 0 }}>
        {/* Section 1 — Remaining Points */}
        <Box px={1.5}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography sx={{ fontSize: "18px" }} color={colors.secondary}>
              Remaining Points:
            </Typography>
            <Typography
              variant="h6"
              sx={{
                background: colors.primary,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                fontWeight: 700,
              }}
            >
              {remainingPoints.toLocaleString("en-IN")}
            </Typography>
          </Box>

          {/* Progress Bar */}
          <LinearProgress
            variant="determinate"
            value={100 - progress}
            sx={{
              height: 6,
              borderRadius: 3,
              backgroundColor: "#ffe0b2", // track background (works fine)
              "& .MuiLinearProgress-bar": {
                background: colors.primary, // ✅ use `background` not `backgroundColor`
              },
              my: 1.5,
            }}
          />
        </Box>

        {/* Section 2 — Points Info */}
        <Box px={1.5}>
          <Box display="flex" justifyContent="space-between">
            <Box>
              <Typography
                color={colors.secondary}
                sx={{ fontSize: "16px", fontWeight: 400 }}
              >
                Remaining Points:
              </Typography>
              <Typography sx={{ fontWeight: 600, fontSize: "16px" }}>
                {remainingPoints.toLocaleString("en-IN")}
              </Typography>
            </Box>
            <Box textAlign="right">
              <Typography
                color={colors.secondary}
                sx={{ fontSize: "16px", fontWeight: 400 }}
              >
                Points Spent:
              </Typography>
              <Typography sx={{ fontWeight: 600, textAlign: "left" }}>
                {pointsSpent.toLocaleString("en-IN")}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Divider before players purchased */}
        <Divider sx={fullWidthDivider} />

        {/* Section 3 — Players Purchased */}
        <Box
          px={1.5}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography sx={{ fontWeight: 500, fontSize: "16px" }}>
            Players Purchased:
          </Typography>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: "16px",
              background: colors.primary,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            {playersPurchased}
          </Typography>
        </Box>

        {/* Section 4 — Remaining Slots */}
        <Box
          px={1.5}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mt={1}
        >
          <Typography sx={{ fontWeight: 500, fontSize: "16px" }}>
            Players Remaining:
          </Typography>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: "16px",
              background: colors.primary,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            {slotsRemaining}
          </Typography>
        </Box>
      </Box>
    </Card>
  );
};

export default TeamCard;
